import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SearchableSelect from '../../components/SearchableSelect';
import SearchableMultiSelect from '../../components/SearchableMultiSelect';
import SuccessModal from '../../components/SuccessModal';

const DEPT_OPTIONS = [
  { value: 'define', label: 'Define', color: '#1e40af', bg: '#dbeafe' },
  { value: 'voa', label: 'Voa', color: '#a16207', bg: '#fef9c3' },
  { value: 'immersion', label: 'Immersion', color: '#991b1b', bg: '#fee2e2' },
];

const TYPE_OPTIONS = [
  { value: 'appel_offre', label: 'Appel d\'Offre', desc: 'Réponse à un appel d\'offre — démarre en étape AO', startStep: 'ao' },
  { value: 'contrat', label: 'Contrat', desc: 'Contrat-cadre avec solde global — démarre en étape Setup', startStep: 'setup' },
  { value: 'consultation_restreinte', label: 'Consultation Restreinte', desc: 'Consultation sur invitation — démarre en étape AO', startStep: 'ao' },
];

const ProjectCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdProjectData, setCreatedProjectData] = useState(null);

  // Client search + create
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [creatingClient, setCreatingClient] = useState(false);
  const clientDropdownRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    client_id: null,
    department: '',
    project_type: '',
    assignee_ids: [],
    solde_global: '',
    ao_deadline: '',
    setup_deadline: '',
    date_debut: '',
    date_fin: '',
    notes: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, uRes] = await Promise.all([
          api.get('/projects/clients'),
          api.get('/projects/assignable-users'),
        ]);
        setClients(cRes.data.data || []);
        setAssignableUsers(uRes.data.data || []);
      } catch (e) {
        console.error('Error loading form data:', e);
      }
    };
    load();
  }, []);

  // Close client dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    set('client_id', client.id);
    setClientSearch(client.name);
    setShowClientDropdown(false);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    set('client_id', null);
    setClientSearch('');
  };

  const handleCreateClient = async () => {
    const name = clientSearch.trim();
    if (!name) return;
    try {
      setCreatingClient(true);
      const res = await api.post('/projects/clients', { name });
      const newClient = res.data.data;
      setClients(prev => [...prev, newClient].sort((a, b) => a.name.localeCompare(b.name)));
      handleClientSelect(newClient);
    } catch (err) {
      const msg = err.response?.data?.errors?.name?.[0] || 'Erreur lors de la création du client';
      setError(msg);
    } finally {
      setCreatingClient(false);
    }
  };

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const selectedType = TYPE_OPTIONS.find(t => t.value === form.project_type);
  const startStep = selectedType?.startStep || 'ao';
  const isAO = form.project_type === 'appel_offre' || form.project_type === 'consultation_restreinte';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.name || !form.department || !form.project_type) {
      setError('Veuillez remplir le nom, le département et le type de projet.');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/projects', {
        name: form.name,
        client_id: form.client_id || null,
        department: form.department,
        project_type: form.project_type,
        assigned_to_id: form.assignee_ids.length > 0 ? form.assignee_ids[0] : null,
        assignee_ids: form.assignee_ids,
        solde_global: form.project_type === 'contrat' && form.solde_global ? parseFloat(form.solde_global) : null,
        ao_deadline: isAO && form.ao_deadline ? form.ao_deadline : null,
        setup_deadline: !isAO && form.setup_deadline ? form.setup_deadline : null,
        date_debut: form.date_debut || null,
        date_fin: form.date_fin || null,
        notes: form.notes || null,
      });
      setCreatedProjectData({ id: res.data.data?.id, type: form.project_type });
      setShowSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
      <h5 style={{ fontWeight: 700, marginBottom: 24, fontSize: '1.15rem' }}>Nouveau projet</h5>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
          padding: '8px 14px', fontSize: '0.8rem', color: '#991b1b', marginBottom: 16,
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* ── Section 1: Identité ── */}
        <Section title="Identité du projet">
          <Field label="Nom du projet" required>
            <input
              className="form-input"
              type="text"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Ex: Enquête satisfaction BCP 2026"
            />
          </Field>

          <Field label="Client">
            <div ref={clientDropdownRef} style={{ position: 'relative' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  className="form-input"
                  type="text"
                  value={clientSearch}
                  onChange={e => { setClientSearch(e.target.value); setShowClientDropdown(true); if (selectedClient) { setSelectedClient(null); set('client_id', null); } }}
                  onFocus={() => setShowClientDropdown(true)}
                  placeholder="Rechercher ou créer un client..."
                />
                {selectedClient && (
                  <button type="button" onClick={handleClearClient}
                    style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '0 4px' }}>
                    <FontAwesomeIcon icon={faTimes} size="sm" />
                  </button>
                )}
              </div>
              {showClientDropdown && clientSearch.trim() && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 200, overflowY: 'auto', marginTop: 2,
                }}>
                  {filteredClients.map(c => (
                    <div
                      key={c.id}
                      onClick={() => handleClientSelect(c)}
                      style={{
                        padding: '7px 12px', cursor: 'pointer', fontSize: '0.8rem',
                        background: selectedClient?.id === c.id ? '#f3f4f6' : 'transparent',
                      }}
                      onMouseEnter={e => e.target.style.background = '#f9fafb'}
                      onMouseLeave={e => e.target.style.background = selectedClient?.id === c.id ? '#f3f4f6' : 'transparent'}
                    >
                      {c.name}
                    </div>
                  ))}
                  {filteredClients.length === 0 && (
                    <div
                      onClick={handleCreateClient}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', fontSize: '0.8rem',
                        color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6,
                      }}
                      onMouseEnter={e => e.target.style.background = '#fef2f2'}
                      onMouseLeave={e => e.target.style.background = 'transparent'}
                    >
                      {creatingClient ? (
                        <><FontAwesomeIcon icon={faSpinner} spin size="sm" /> Création...</>
                      ) : (
                        <><FontAwesomeIcon icon={faPlus} size="sm" /> Créer "{clientSearch.trim()}"</>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Field>

          {/* Client abbreviation (inline) — shown when a client is selected */}
          {selectedClient && (
            <Field label={`Abréviation du client (${selectedClient.name})`}>
              <input
                className="form-input"
                type="text"
                maxLength={20}
                defaultValue={selectedClient.abbreviation || ''}
                placeholder="Auto-calculée si vide (ex. CAM pour Crédit Agricole du Maroc)"
                onBlur={async (e) => {
                  const val = e.target.value.trim();
                  if (val === (selectedClient.abbreviation || '')) return;
                  try {
                    const res = await api.put(`/projects/clients/${selectedClient.id}`, { abbreviation: val || null });
                    const updated = res.data.data;
                    setClients(prev => prev.map(c => c.id === updated.id ? updated : c));
                    setSelectedClient(updated);
                  } catch { }
                }}
              />
              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 4 }}>
                Laisser vide pour utiliser l'abréviation calculée automatiquement (initiales des mots, en excluant et, de, du, des, le, la, les).
              </div>
            </Field>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Department selector */}
            <Field label="Département" required>
              <div style={{ display: 'flex', gap: 8 }}>
                {DEPT_OPTIONS.map(d => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => set('department', d.value)}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 6,
                      border: form.department === d.value ? `2px solid ${d.color}` : '1px solid #d1d5db',
                      background: form.department === d.value ? d.bg : '#fff',
                      color: form.department === d.value ? d.color : '#6b7280',
                      fontWeight: form.department === d.value ? 700 : 500,
                      fontSize: '0.78rem',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </Field>

            {/* Type selector */}
            <Field label="Type" required>
              <select
                className="form-input"
                value={form.project_type}
                onChange={e => set('project_type', e.target.value)}
              >
                <option value="">Choisir le type...</option>
                {TYPE_OPTIONS.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {selectedType && (
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 4 }}>
                  {selectedType.desc}
                </div>
              )}
            </Field>
          </div>
        </Section>

        {/* ── Section 2: Assignment & solde ── */}
        <Section title="Affectation">
          <Field label="Personnes assignées">
            <SearchableMultiSelect
              options={assignableUsers}
              selectedValues={form.assignee_ids}
              onChange={ids => set('assignee_ids', ids)}
              placeholder="Ajouter des personnes..."
              getOptionLabel={u => u.name}
              getOptionMeta={u => u.role_display || u.role}
              triggerClassName="px-3 py-2"
            />
          </Field>

          {form.project_type === 'contrat' && (
            <Field label="Solde global du contrat (MAD)">
              <input
                className="form-input"
                type="number"
                value={form.solde_global}
                onChange={e => set('solde_global', e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </Field>
          )}
        </Section>

        {/* ── Section 3: Deadline (single, based on type) ── */}
        {form.project_type && (
          <Section title="Échéance">
            {isAO ? (
              <Field label="Date et heure limite AO">
                <input
                  className="form-input"
                  type="datetime-local"
                  value={form.ao_deadline}
                  onChange={e => set('ao_deadline', e.target.value)}
                />
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 4 }}>
                  Date et heure limites pour la soumission de l'appel d'offre
                </div>
              </Field>
            ) : (
              <Field label="Date limite Setup">
                <input
                  className="form-input"
                  type="date"
                  value={form.setup_deadline}
                  onChange={e => set('setup_deadline', e.target.value)}
                />
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 4 }}>
                  Date limite pour la phase de mise en place
                </div>
              </Field>
            )}
          </Section>
        )}

        {/* ── Section 4: Dates ── */}
        <Section title="Dates du projet">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Date début">
              <input
                className="form-input"
                type="date"
                value={form.date_debut}
                onChange={e => set('date_debut', e.target.value)}
              />
            </Field>
            <Field label="Date fin">
              <input
                className="form-input"
                type="date"
                value={form.date_fin}
                onChange={e => set('date_fin', e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* ── Section 5: Notes ── */}
        <Section title="Notes">
          <textarea
            className="form-input"
            rows={3}
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            placeholder="Remarques, contexte, instructions..."
          />
        </Section>

        {/* ── Actions ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8, paddingBottom: 32 }}>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            style={{
              padding: '8px 20px', borderRadius: 6, border: '1px solid #d1d5db',
              background: '#fff', color: '#374151', fontSize: '0.8rem', fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '8px 24px', borderRadius: 6, border: 'none',
              background: loading ? '#9ca3af' : '#dc2626', color: '#fff',
              fontSize: '0.8rem', fontWeight: 600, cursor: loading ? 'default' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {loading ? 'Création...' : 'Créer le projet'}
          </button>
        </div>
      </form>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          if (createdProjectData?.type === 'contrat' && createdProjectData?.id) {
            navigate('/projects', { state: { openSlotsForProject: createdProjectData.id } });
          } else {
            navigate('/projects');
          }
        }}
        title="Projet créé"
        message={`Le projet "${form.name}" a été créé avec succès.`}
        duration={2000}
      />

      <style>{`
        .form-input {
          width: 100%;
          font-size: 0.82rem;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          outline: none;
          background: #fff;
          color: #111827;
          transition: border-color 0.15s;
        }
        .form-input:focus {
          border-color: #dc2626;
        }
        .form-input::placeholder {
          color: #9ca3af;
        }
        select.form-input {
          cursor: pointer;
        }
        textarea.form-input {
          resize: vertical;
          min-height: 70px;
        }
      `}</style>
    </div>
  );
};

/* ── Helpers ── */

const Section = ({ title, children }) => (
  <div style={{
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '16px 20px',
    marginBottom: 16,
  }}>
    <div style={{
      fontSize: '0.75rem',
      fontWeight: 700,
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      marginBottom: 14,
    }}>
      {title}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {children}
    </div>
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 5 }}>
      {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
    </div>
    {children}
  </div>
);

export default ProjectCreate;
